<script>
import { auth } from '@/firebase'
import { sendPasswordResetEmail } from 'firebase/auth'

export default {
  name: 'ResetPassword',

  emits: ['close'],

  data: () => ({
    email: '',
    isLoading: false,
    error: null,
    success: false,
    rateLimitInfo: null,
    rateLimitTimer: null,
  }),

  computed: {
    canSubmit() {
      return (
        this.email &&
        this.email.includes('@') &&
        !this.isLoading &&
        !this.isRateLimited()
      )
    },

    rateLimitMessage() {
      if (!this.rateLimitInfo) return null
      const minutes = Math.ceil(this.rateLimitInfo.remainingSeconds / 60)
      return `Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before requesting another reset`
    },
  },

  mounted() {
    this.checkRateLimit()
  },

  beforeUnmount() {
    if (this.rateLimitTimer) {
      clearInterval(this.rateLimitTimer)
    }
  },

  methods: {
    isRateLimited() {
      const lastRequest = localStorage.getItem('password_reset_last_request')
      if (!lastRequest) return false

      const RATE_LIMIT_SECONDS = 300 // 5 minutes
      const elapsed = (Date.now() - parseInt(lastRequest)) / 1000

      if (elapsed < RATE_LIMIT_SECONDS) {
        this.rateLimitInfo = {
          remainingSeconds: Math.ceil(RATE_LIMIT_SECONDS - elapsed),
        }
        return true
      }

      this.rateLimitInfo = null
      return false
    },

    checkRateLimit() {
      if (this.isRateLimited()) {
        // Update countdown every second
        this.rateLimitTimer = setInterval(() => {
          if (!this.isRateLimited()) {
            clearInterval(this.rateLimitTimer)
          }
        }, 1000)
      }
    },

    async handleSubmit() {
      // Final rate limit check
      if (this.isRateLimited()) {
        this.error = this.rateLimitMessage
        return
      }

      this.isLoading = true
      this.error = null

      try {
        // Firebase password reset
        await sendPasswordResetEmail(auth, this.email, {
          url: window.location.origin + '/login', // Redirect after reset
          handleCodeInApp: false,
        })

        // Store timestamp for rate limiting
        localStorage.setItem('password_reset_last_request', Date.now().toString())

        // Show success
        this.success = true
        const submittedEmail = this.email
        this.email = '' // Clear email for security

        // Auto-close after 5 seconds
        setTimeout(() => {
          this.$emit('close')
        }, 5000)
      } catch (error) {
        console.error('Password reset error:', error)

        // Handle specific Firebase errors
        switch (error.code) {
          case 'auth/user-not-found':
            // Don't reveal if user exists for security
            this.success = true
            this.email = ''
            break
          case 'auth/invalid-email':
            this.error = 'Please enter a valid email address'
            break
          case 'auth/too-many-requests':
            this.error = 'Too many attempts. Please try again later.'
            localStorage.setItem(
              'password_reset_last_request',
              Date.now().toString()
            )
            break
          default:
            this.error = 'Unable to send reset email. Please try again.'
        }
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<template>
  <div class="box" style="max-width: 500px; margin: 2rem auto">
    <!-- Header -->
    <div class="has-text-centered mb-5">
      <h1 class="title is-4">Reset Password</h1>
      <p class="subtitle is-6 has-text-grey">
        Enter your email and we'll send you a link to reset your password
      </p>
    </div>

    <!-- Success State -->
    <div v-if="success" class="notification is-success">
      <p class="has-text-weight-semibold mb-2">
        <b-icon icon="check-circle" size="is-small"></b-icon>
        Check your email
      </p>
      <p>
        If an account exists for that email, you'll receive password reset
        instructions shortly.
      </p>
      <p class="mt-3 is-size-7 has-text-grey-dark">
        Didn't receive it? Check your spam folder or try again in 5 minutes.
      </p>
    </div>

    <!-- Form -->
    <form v-else @submit.prevent="handleSubmit">
      <b-field
        label="Email Address"
        :type="error ? 'is-danger' : ''"
        :message="error || rateLimitMessage">
        <b-input
          v-model="email"
          type="email"
          placeholder="your@email.com"
          size="is-medium"
          icon="email"
          :disabled="isLoading || isRateLimited()"
          required
          autofocus />
      </b-field>

      <!-- Rate Limit Warning -->
      <div v-if="rateLimitInfo" class="notification is-warning is-light">
        <b-icon icon="clock-outline" size="is-small"></b-icon>
        {{ rateLimitMessage }}
      </div>

      <!-- Actions -->
      <div class="buttons is-justify-content-flex-end mt-5">
        <b-button data-test="cancelBtn" @click="$emit('close')"> Cancel </b-button>
        <b-button
          data-test="resetSubmitBtn"
          type="is-primary"
          native-type="submit"
          :loading="isLoading"
          :disabled="!canSubmit">
          Send Reset Link
        </b-button>
      </div>

      <!-- Help Text -->
      <div class="has-text-centered mt-4">
        <p class="is-size-7 has-text-grey">
          Remember your password?
          <a @click="$emit('close')">Sign in</a>
        </p>
      </div>
    </form>
  </div>
</template>

<style scoped>
.notification {
  border-radius: 6px;
}
</style>
